import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Send, Search, Settings, X } from "lucide-react";
import EmailTemplateManager from "./EmailTemplateManager";

const API = process.env.REACT_APP_API_URL;

const EmailCampaignsPanel = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [plantillas, setPlantillas] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [mostrarGestor, setMostrarGestor] = useState(false);

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [search, setSearch] = useState("");
  const [segmento, setSegmento] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);

  const [correoNuevo, setCorreoNuevo] = useState("");
  const [correosSueltos, setCorreosSueltos] = useState([]);

  const [enviando, setEnviando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const cargarPlantillas = () => {
    axios
      .get(`${API}/admin/plantillas-email`, { headers })
      .then((res) => setPlantillas(res.data))
      .catch(() => toast.error("No se pudieron cargar las plantillas"));
  };

  useEffect(() => {
    cargarPlantillas();
    axios
      .get(`${API}/admin/empresas`, { headers })
      .then((res) => setEmpresas(res.data))
      .catch(() => toast.error("No se pudieron cargar las empresas"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (empresaId) params.empresaId = empresaId;
    if (segmento) params.segmento = segmento;

    const timeout = setTimeout(() => {
      axios
        .get(`${API}/admin/campanias-email/usuarios`, { headers, params })
        .then((res) => setUsuarios(res.data))
        .catch(() => toast.error("No se pudieron cargar los usuarios"));
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, empresaId, segmento]);

  const cargarHistorial = (p = 1) => {
    axios
      .get(`${API}/admin/campanias-email`, { headers, params: { page: p } })
      .then((res) => {
        setHistorial(res.data.data);
        setPage(res.data.page);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => toast.error("No se pudo cargar el historial"));
  };

  useEffect(() => {
    cargarHistorial(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSeleccionado = (usuario) => {
    setSeleccionados((prev) =>
      prev.some((u) => u._id === usuario._id)
        ? prev.filter((u) => u._id !== usuario._id)
        : [...prev, usuario],
    );
  };

  const aplicarSegmento = (valor) => {
    setSegmento((prev) => (prev === valor ? "" : valor));
  };

  const agregarCorreoSuelto = () => {
    const correo = correoNuevo.trim().toLowerCase();
    if (!correo) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      toast.error("Correo inválido");
      return;
    }
    if (correosSueltos.includes(correo)) {
      setCorreoNuevo("");
      return;
    }
    setCorreosSueltos((prev) => [...prev, correo]);
    setCorreoNuevo("");
  };

  const quitarCorreoSuelto = (correo) => {
    setCorreosSueltos((prev) => prev.filter((c) => c !== correo));
  };

  const totalDestinatarios = seleccionados.length + correosSueltos.length;

  const enviar = async () => {
    if (!templateId) {
      toast.error("Selecciona una plantilla");
      return;
    }
    if (totalDestinatarios === 0) {
      toast.error("Selecciona al menos un destinatario");
      return;
    }

    const destinatarios = [
      ...seleccionados.map((u) => ({ email: u.email, nombre: u.name })),
      ...correosSueltos.map((email) => ({ email, nombre: "" })),
    ];

    setEnviando(true);
    try {
      await axios.post(
        `${API}/admin/campanias-email/enviar`,
        { templateId, destinatarios },
        { headers },
      );
      toast.success("Campaña enviada");
      setSeleccionados([]);
      setCorreosSueltos([]);
      cargarHistorial(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar la campaña");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <h1 className="text-xl font-semibold text-gray-800 mb-6">
        Enviar campaña de email
      </h1>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 mb-8">
        <div className="flex gap-2">
          <select
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">Selecciona una plantilla</option>
            {plantillas.map((p) => (
              <option key={p._id} value={p._id}>
                {p.nombre}
              </option>
            ))}
          </select>
          <button
            onClick={() => setMostrarGestor(true)}
            className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50"
          >
            <Settings className="h-4 w-4" /> Gestionar plantillas
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => aplicarSegmento("inactivos")}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              segmento === "inactivos"
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Inactivos (+30d)
          </button>
          <button
            type="button"
            onClick={() => aplicarSegmento("free")}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              segmento === "free"
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Plan FREE
          </button>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              placeholder="Buscar por nombre o email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            <option value="">Todas las empresas</option>
            {empresas.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.razon_social}
              </option>
            ))}
          </select>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {usuarios.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Sin usuarios para este filtro
            </p>
          ) : (
            usuarios.map((u) => (
              <label
                key={u._id}
                className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={seleccionados.some((s) => s._id === u._id)}
                  onChange={() => toggleSeleccionado(u)}
                />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{u.name}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
              </label>
            ))
          )}
        </div>

        <div>
          <div className="flex gap-2">
            <input
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Agregar correo nuevo (no registrado)"
              value={correoNuevo}
              onChange={(e) => setCorreoNuevo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  agregarCorreoSuelto();
                }
              }}
            />
            <button
              type="button"
              onClick={agregarCorreoSuelto}
              className="text-sm text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50"
            >
              Agregar
            </button>
          </div>
          {correosSueltos.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {correosSueltos.map((correo) => (
                <span
                  key={correo}
                  className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1"
                >
                  {correo}
                  <button onClick={() => quitarCorreoSuelto(correo)} aria-label="Quitar">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {totalDestinatarios} destinatario(s)
          </span>
          <button
            onClick={enviar}
            disabled={enviando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            <Send className="h-4 w-4" />
            {enviando ? "Enviando..." : "Enviar campaña"}
          </button>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Plantilla</th>
              <th className="text-left px-4 py-2">Asunto</th>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Destinatarios</th>
              <th className="text-left px-4 py-2">Éxitos/Fallos</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((c) => (
              <tr key={c._id} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-800">{c.templateNombre}</td>
                <td className="px-4 py-2 text-gray-600">{c.asunto}</td>
                <td className="px-4 py-2 text-gray-600">
                  {new Date(c.createdAt).toLocaleString("es-CO")}
                </td>
                <td className="px-4 py-2 text-gray-600">{c.destinatarios.length}</td>
                <td className="px-4 py-2 text-gray-600">
                  {c.exitosos} / {c.fallidos}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => cargarHistorial(p)}
              className={`px-3 py-1 rounded-lg text-sm ${
                p === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {mostrarGestor && (
        <EmailTemplateManager onClose={() => setMostrarGestor(false)} onChanged={cargarPlantillas} />
      )}
    </>
  );
};

export default EmailCampaignsPanel;
